import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import {
  Download as DownloadIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import axios from 'axios';

// Ensure we always hit the /api namespace even if REACT_APP_API_URL is just the host
const RAW_API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE_URL = `${RAW_API_BASE.replace(/\/$/, '')}/api`;

interface MediaKitData {
  prId: string;
  title: string;
  content: string;
  company: string;
  contactInfo: {
    name: string;
    email: string;
    phone: string;
    company: string;
  };
  author: {
    name: string;
    email: string;
    company: string;
    website: string;
  };
  industry: string;
  createdAt: string;
  thumbnailUrl?: string | null;
  assets: any[];
}

const MediaKit: React.FC = () => {
  const { prId, companySlug, campaignId } = useParams<{ prId?: string; companySlug?: string; campaignId?: string }>();
  const [searchParams] = useSearchParams();
  const prIdFromQuery = searchParams.get('prId');
  const campaignIdFromQuery = searchParams.get('campaignId');
  const prIdsFromQuery = searchParams.get('prIds'); // Multiple PR IDs for campaigns
  
  const [mediaKitData, setMediaKitData] = useState<MediaKitData | null>(null);
  const [campaignPRs, setCampaignPRs] = useState<any[]>([]);
  const [currentPRIndex, setCurrentPRIndex] = useState(0);
  const [campaignInfo, setCampaignInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMediaKit();
  }, [prId, companySlug, campaignId, campaignIdFromQuery, prIdFromQuery, prIdsFromQuery, currentPRIndex]);

  const fetchMediaKit = async () => {
    try {
      setLoading(true);
      setError(null);

      const finalCampaignId = campaignIdFromQuery || campaignId;
      const finalPrId = prIdFromQuery || prId || '';

      let mediaKitLoaded = false;

      // If campaign ID is provided, fetch campaign and all PRs
      if (finalCampaignId) {
        try {
          const token = localStorage.getItem('token');
          const campaignResponse = await axios.get(`${API_BASE_URL}/campaigns/${finalCampaignId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (campaignResponse.data.success) {
            const campaign = campaignResponse.data.campaign;
            setCampaignInfo(campaign);
            
            // Get all PRs from campaign - if prIds query param exists, use those; otherwise use campaign PRs
            let prs = campaign.pressReleases || [];
            
            // If prIds query parameter is provided, fetch those specific PRs via media kit endpoint (public)
            if (prIdsFromQuery) {
              const prIdsArray = prIdsFromQuery.split(',').filter(id => id.trim());
              if (prIdsArray.length > 0) {
                try {
                  const prsPromises = prIdsArray.map(async (id) => {
                    try {
                      // Use public media kit endpoint instead of authenticated PR endpoint
                      const mediaKitResponse = await axios.get(`${API_BASE_URL}/media-kit/${id}`);
                      if (mediaKitResponse.data.success && mediaKitResponse.data.mediaKit) {
                        const mk = mediaKitResponse.data.mediaKit;
                        return {
                          _id: mk.prId,
                          id: mk.prId,
                          title: mk.title,
                          content: mk.content,
                          status: 'published'
                        };
                      }
                      return null;
                    } catch (err) {
                      console.error(`Error fetching media kit for PR ${id}:`, err);
                      return null;
                    }
                  });
                  const fetchedPRs = (await Promise.all(prsPromises)).filter(pr => pr !== null);
                  if (fetchedPRs.length > 0) {
                    prs = fetchedPRs;
                  }
                } catch (err) {
                  console.error('Error fetching PRs from query params:', err);
                  // Fall back to campaign PRs
                }
              }
            }
            
            setCampaignPRs(prs);

            // Use current PR index or first PR
            const activePRId = prs[currentPRIndex]?._id || prs[currentPRIndex]?.id || prs[0]?._id || prs[0]?.id || finalPrId;
            
            if (!activePRId && prs.length === 0) {
              setError('No press releases found in this campaign.');
              setLoading(false);
              return;
            }

            // Fetch media kit for the active PR
            const apiUrl = `${API_BASE_URL}/media-kit/${activePRId}`;
            const response = await axios.get(apiUrl);
            
            if (response.data.success) {
              setMediaKitData(response.data.mediaKit);
              mediaKitLoaded = true;
            } else {
              setError(response.data.message || 'Failed to load media kit');
            }
          }
        } catch (err: any) {
          console.error('Error fetching campaign:', err);
          // If campaign fetch fails but we have prIds, try fetching PRs directly via media kit endpoint
          if (prIdsFromQuery) {
            const prIdsArray = prIdsFromQuery.split(',').filter(id => id.trim());
            if (prIdsArray.length > 0) {
              try {
                // Use public media kit endpoint instead of authenticated PR endpoint
                const prsPromises = prIdsArray.map(async (id) => {
                  try {
                    const mediaKitResponse = await axios.get(`${API_BASE_URL}/media-kit/${id}`);
                    if (mediaKitResponse.data.success && mediaKitResponse.data.mediaKit) {
                      const mk = mediaKitResponse.data.mediaKit;
                      return {
                        _id: mk.prId,
                        id: mk.prId,
                        title: mk.title,
                        content: mk.content,
                        status: 'published'
                      };
                    }
                    return null;
                  } catch (prErr) {
                    console.error(`Error fetching media kit for PR ${id}:`, prErr);
                    return null;
                  }
                });
                const fetchedPRs = (await Promise.all(prsPromises)).filter(pr => pr !== null);
                if (fetchedPRs.length > 0) {
                  setCampaignPRs(fetchedPRs);
                  const activePRId = fetchedPRs[0]?._id || fetchedPRs[0]?.id;
                  if (activePRId) {
                    const apiUrl = `${API_BASE_URL}/media-kit/${activePRId}`;
                    const response = await axios.get(apiUrl);
                    if (response.data.success) {
                      setMediaKitData(response.data.mediaKit);
                      setLoading(false);
                      return;
                    }
                  }
                }
              } catch (prsErr) {
                console.error('Error fetching PRs directly:', prsErr);
              }
            }
          }
          // Fall through to single PR fetch
        }
      }

      // If no campaign or campaign fetch failed, fetch single PR
      if (!finalCampaignId || !mediaKitLoaded) {
        // If we have prIds but no campaign, try to fetch those PRs directly via media kit endpoint
        if (prIdsFromQuery && !finalCampaignId) {
          const prIdsArray = prIdsFromQuery.split(',').filter(id => id.trim());
          if (prIdsArray.length > 0) {
            try {
              // Use public media kit endpoint instead of authenticated PR endpoint
              const prsPromises = prIdsArray.map(async (id) => {
                try {
                  const mediaKitResponse = await axios.get(`${API_BASE_URL}/media-kit/${id}`);
                  if (mediaKitResponse.data.success && mediaKitResponse.data.mediaKit) {
                    const mk = mediaKitResponse.data.mediaKit;
                    return {
                      _id: mk.prId,
                      id: mk.prId,
                      title: mk.title,
                      content: mk.content,
                      status: 'published'
                    };
                  }
                  return null;
                } catch (err) {
                  console.error(`Error fetching media kit for PR ${id}:`, err);
                  return null;
                }
              });
              const fetchedPRs = (await Promise.all(prsPromises)).filter(pr => pr !== null);
              if (fetchedPRs.length > 0) {
                setCampaignPRs(fetchedPRs);
                const activePRId = fetchedPRs[0]?._id || fetchedPRs[0]?.id;
                if (activePRId) {
                  const apiUrl = `${API_BASE_URL}/media-kit/${activePRId}`;
                  const response = await axios.get(apiUrl);
                  if (response.data.success) {
                    setMediaKitData(response.data.mediaKit);
                    setLoading(false);
                    return;
                  }
                }
              }
            } catch (err) {
              console.error('Error fetching PRs from query params:', err);
            }
          }
        }
        
        if (!finalPrId) {
          const errorMsg = 'PR ID or Campaign ID is required.';
          setError(errorMsg);
          setLoading(false);
          return;
        }

        const apiUrl = `${API_BASE_URL}/media-kit/${finalPrId}`;
        let response;
        try {
          response = await axios.get(apiUrl);
        } catch (err: any) {
          if (err.response?.status === 404) {
            const fallbackUrl = `${API_BASE_URL}/media-kit/${finalPrId}?prId=${finalPrId}`;
            response = await axios.get(fallbackUrl);
          } else {
            throw err;
          }
        }
        
        if (response.data.success) {
          setMediaKitData(response.data.mediaKit);
        } else {
          setError(response.data.message || 'Failed to load media kit');
        }
      }
    } catch (err: any) {
      console.error('❌ MediaKit - Error fetching media kit:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load media kit.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!mediaKitData) return;

    // Create a formatted document for download
    const docContent = `
PRESS RELEASE

${mediaKitData.title}

Company: ${mediaKitData.company}
Industry: ${mediaKitData.industry}
Date: ${new Date(mediaKitData.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${mediaKitData.content.replace(/<[^>]*>/g, '').replace(/\n\n+/g, '\n\n')}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `.trim();

    // Create blob and download as PDF-ready text file
    const blob = new Blob([docContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const sanitizedTitle = mediaKitData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    link.download = `${sanitizedTitle}_press_release_${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #09273b 0%, #0927eb 100%)',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress sx={{ color: '#ff5200' }} />
        <Typography variant="body1" sx={{ color: 'white' }}>
          Loading media kit...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: 'linear-gradient(135deg, #09273b 0%, #0927eb 100%)', py: 4 }}>
        <Container maxWidth="md">
          <Paper elevation={4} sx={{ p: 4, border: '2px solid #ff5200' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#09273b', fontWeight: 700 }}>Error Loading Media Kit</Typography>
              <Typography variant="body1" sx={{ color: '#1a202c' }}>{error}</Typography>
              {(prId || prIdFromQuery) && (
                <Typography variant="body2" sx={{ mt: 1, color: '#4A5568' }}>
                  PR ID: {prId || prIdFromQuery}
                </Typography>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, color: '#09273b', fontWeight: 600 }}>Please check:</Typography>
                <Box component="ul" sx={{ marginTop: 1, marginBottom: 0, paddingLeft: 3, color: '#4A5568' }}>
                  <li>The PR ID is correct</li>
                  <li>The backend server is running on port 5000</li>
                  <li>Check the browser console for detailed error messages</li>
                </Box>
              </Box>
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!mediaKitData) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: 'linear-gradient(135deg, #09273b 0%, #0927eb 100%)', py: 4 }}>
        <Container maxWidth="md">
          <Paper elevation={4} sx={{ p: 4, border: '2px solid #ff5200' }}>
            <Alert severity="warning">
              <Typography variant="h6" sx={{ color: '#09273b', fontWeight: 700 }}>Media kit not found</Typography>
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#09273b', py: 4 }}>
      <Container maxWidth="lg">
        {/* Campaign Info Banner */}
        {(campaignInfo || campaignPRs.length > 1) && (
          <Paper elevation={2} sx={{ 
            p: 3, 
            mb: 3, 
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <Grid container spacing={2} alignItems="center">
              {campaignInfo && (
                <Grid item xs={12} md={campaignPRs.length > 1 ? 6 : 12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: campaignPRs.length > 1 ? 2 : 0 }}>
                    <BusinessIcon sx={{ fontSize: '32px', color: '#ff5200' }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', mb: 0.5 }}>
                        {campaignInfo.name || 'Campaign'}
                      </Typography>
                      {campaignInfo.startDate && campaignInfo.endDate && (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Duration: {new Date(campaignInfo.startDate).toLocaleDateString()} - {new Date(campaignInfo.endDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
              {campaignPRs.length > 1 && (
                <Grid item xs={12} md={campaignInfo ? 6 : 12}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.9)', fontWeight: 'bold' }}>
                      Viewing {campaignPRs.length} Press Releases
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {campaignPRs.map((pr, index) => {
                        const prId = pr._id || pr.id;
                        const isActive = index === currentPRIndex;
                        return (
                          <Chip
                            key={prId}
                            label={`PR ${index + 1}: ${pr.title?.substring(0, 30)}${pr.title?.length > 30 ? '...' : ''}`}
                            onClick={() => {
                              setCurrentPRIndex(index);
                              fetchMediaKit();
                            }}
                            sx={{
                              backgroundColor: isActive ? '#ff5200' : 'rgba(255,255,255,0.2)',
                              color: 'white',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: isActive ? '#e64a00' : 'rgba(255,255,255,0.3)'
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1, display: 'block' }}>
                      Currently viewing: PR {currentPRIndex + 1} of {campaignPRs.length}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}
        
        {/* Show All PRs Section if Campaign */}
        {campaignPRs.length > 1 && (
          <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#09273b' }}>
              All Press Releases in Campaign ({campaignPRs.length})
            </Typography>
            <Grid container spacing={2}>
              {campaignPRs.map((pr, index) => {
                const prId = pr._id || pr.id;
                const isActive = index === currentPRIndex;
                return (
                  <Grid item xs={12} sm={6} md={4} key={prId}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        border: isActive ? '2px solid #ff5200' : '1px solid #e0e0e0',
                        backgroundColor: isActive ? 'rgba(255, 82, 0, 0.05)' : 'white',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#ff5200',
                          transform: 'translateY(-2px)',
                          boxShadow: 3
                        }
                      }}
                      onClick={() => {
                        setCurrentPRIndex(index);
                        fetchMediaKit();
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip 
                            label={`PR ${index + 1}`}
                            size="small"
                            sx={{
                              backgroundColor: isActive ? '#ff5200' : '#e0e0e0',
                              color: isActive ? 'white' : '#666',
                              fontWeight: 'bold'
                            }}
                          />
                          {isActive && (
                            <Chip 
                              label="Viewing"
                              size="small"
                              sx={{
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                fontSize: '0.7rem'
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#09273b' }}>
                          {pr.title || `Press Release ${index + 1}`}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          Status: {pr.status || 'draft'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}

        {/* Header Section */}
        <Paper elevation={3} sx={{ p: 4, mb: 3, backgroundColor: '#09273b', color: 'white', border: '2px solid #ff5200' }}>
          {/* Main Heading */}
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 700, 
              mb: 4, 
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              color: '#ffffff',
              textAlign: 'left'
            }}
          >
            Press Release Media Kit
            {campaignPRs.length > 1 && ` - PR ${currentPRIndex + 1} of ${campaignPRs.length}`}
          </Typography>
          
          {/* PR Image if available */}
          {mediaKitData.thumbnailUrl && (
            <Box sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', border: '2px solid rgba(255,255,255,0.2)' }}>
              <img 
                src={mediaKitData.thumbnailUrl} 
                alt={mediaKitData.title}
                style={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </Box>
          )}
          
          {/* PR Title */}
          <Typography 
            variant="h4" 
            component="h2" 
            sx={{ 
              fontWeight: 600, 
              mb: 3, 
              fontSize: { xs: '1.25rem', md: '1.75rem' },
              color: '#ffffff',
              lineHeight: 1.4
            }}
          >
            {mediaKitData.title}
          </Typography>
          
          {/* Metadata */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 4 }}>
            <Chip 
              label={mediaKitData.company || 'Unnamed company'} 
              sx={{ backgroundColor: 'rgba(255,82,0,0.3)', color: 'white', fontWeight: 600, border: '1px solid rgba(255,82,0,0.5)' }}
              icon={<BusinessIcon sx={{ color: '#ff5200' }} />}
            />
            <Chip 
              label={mediaKitData.industry || 'General'} 
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
            />
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
              {new Date(mediaKitData.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </Typography>
          </Box>
          
          {/* Download Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                backgroundColor: '#ff5200',
                color: 'white',
                fontWeight: 700,
                py: 1.5,
                px: 4,
                fontSize: '16px',
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(255, 82, 0, 0.4)',
                '&:hover': {
                  backgroundColor: '#e64a00',
                  boxShadow: '0 6px 16px rgba(255, 82, 0, 0.5)',
                }
              }}
            >
              Download Press Release
            </Button>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 4, mb: 3, backgroundColor: '#ffffff', border: '2px solid #09273b' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#09273b' }}>
                Press Release
              </Typography>
              <Divider sx={{ mb: 3, borderColor: '#ff5200', borderWidth: '2px' }} />
              <Box 
                sx={{ 
                  '& p': { mb: 2, lineHeight: 1.8, color: '#1a202c', fontSize: '16px' },
                  '& h2, & h3': { mt: 3, mb: 2, fontWeight: 600, color: '#09273b' }
                }}
                dangerouslySetInnerHTML={{ __html: mediaKitData.content.replace(/\n/g, '<br />') }}
              />
              {/* Page indicator */}
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#4A5568', fontStyle: 'italic' }}>
                  -- {campaignPRs.length > 1 ? `${currentPRIndex + 1} of ${campaignPRs.length}` : '1 of 1'} --
                </Typography>
              </Box>
            </Paper>

            {/* Assets Section */}
            {mediaKitData.assets && mediaKitData.assets.length > 0 && (
              <Paper elevation={2} sx={{ p: 4, backgroundColor: '#ffffff', border: '2px solid #09273b' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#09273b' }}>
                  Media Assets
                </Typography>
                <Divider sx={{ mb: 3, borderColor: '#ff5200', borderWidth: '2px' }} />
                <Grid container spacing={2}>
                  {mediaKitData.assets.map((asset, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card sx={{ border: '1px solid #e0e0e0', '&:hover': { borderColor: '#ff5200' } }}>
                        <CardContent>
                          <Typography variant="body2" sx={{ mb: 1, color: '#09273b', fontWeight: 600 }}>
                            {asset.name || `Asset ${index + 1}`}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            variant="outlined"
                            fullWidth
                            sx={{
                              borderColor: '#ff5200',
                              color: '#ff5200',
                              '&:hover': {
                                borderColor: '#e64a00',
                                backgroundColor: 'rgba(255, 82, 0, 0.1)'
                              }
                            }}
                          >
                            Download
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default MediaKit;

